import { AbTest } from '../../../models/AbTest';
import { AbTestType } from '../../../models/AbTestType';
import {
  generateAnalyticsDashboardUrl,
  getAbTestDuplicationTestIdString,
  getAbTestType,
} from '../../../utils/AbTestingUtils';
import { ModalActions } from '../../AbTestModals/hooks/useAbTestModal';
import { Operator, Property } from '@app/react-common/components/rule-engine-v2/models/RuleEngineConfig';
import { SegmentMetrics } from './components/AbTestSegmentsMetricsTable/AbTestSegmentsMetricsTable.types';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import { getKeyByStringValue } from '@utils/string-utils';
import {
  AbTestWinnerRequestDto,
  AbTestWinnerPredictedSegmentGroupsDto,
  AbTestWinnerPredictedSegmentRequestDto,
} from '../../../models/AbTestWinnerRequestDto';
import { SegmentWithState } from '../../review-ab-test-modal/ReviewAbTestModal';
import { TrafficAllocationVariant } from '@app/react-common/components/traffic-allocation/TrafficAllocationShared';
import { getIsThemeTest } from '../../../utils';
import { ABTestOutlierFilterOption } from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutVariantABTest';

export const getAbTestThreeDotMenuItems = (
  abTest: AbTest,
  modalActions: ModalActions,
  angularRedirect: (url: string) => void,
  showToast: (toast: { message: string; mode: string }) => void,
  winningSegmentClean?: string | null,
) => {
  const type = getAbTestType(abTest);

  if (type === AbTestType.DRAFT) {
    return [
      {
        label: 'Start',
        onClick: () => modalActions.open('start'),
      },
      {
        label: 'Edit',
        onClick: () => modalActions.open('edit'),
      },
      {
        label: 'Duplicate',
        onClick: () => modalActions.open('duplicate'),
      },
      {
        label: 'Copy test ID',
        onClick: () => {
          const testId = getAbTestDuplicationTestIdString(abTest);
          if (!navigator?.clipboard) {
            alert(`Your browser does not support copying to clipboard. Test ID is: ${testId}`);
            return;
          }
          navigator.clipboard.writeText(testId).then(() => {
            showToast({ message: 'Test ID copied to clipboard.', mode: 'success' });
          });
        },
      },
      {
        label: 'Delete',
        onClick: () => modalActions.open('delete'),
      },
    ];
  } else if (type === AbTestType.RUNNING) {
    return [
      {
        label: 'View',
        onClick: () => modalActions.open('view'),
      },
      {
        label: 'End test',
        onClick: () => modalActions.open('end'),
      },
      {
        label: 'Get analytics',
        onClick: () => {
          angularRedirect(generateAnalyticsDashboardUrl(abTest));
        },
      },
      {
        label: 'Duplicate',
        onClick: () => modalActions.open('duplicate'),
      },
    ];
  } else if (type === AbTestType.CONCLUDED) {
    return [
      {
        label: 'View',
        onClick: () => modalActions.open('view'),
      },
      {
        label: 'Get analytics',
        onClick: () => {
          angularRedirect(generateAnalyticsDashboardUrl(abTest, winningSegmentClean));
        },
      },
      {
        label: 'Duplicate',
        onClick: () => modalActions.open('duplicate'),
      },
      {
        label: 'Archive',
        onClick: () => modalActions.open('archive'),
      },
      {
        label: 'Set Winner Instructions',
        onClick: () => modalActions.open('setWinnerInstructions'),
        hide: !getIsThemeTest(abTest.testType),
      },
    ];
  }
  return [];
};

export const getAbTestStatusText = (abTest: AbTest): AbTestType => {
  if (abTest.endedAt) return AbTestType.CONCLUDED;
  if (abTest.startedAt) return AbTestType.RUNNING;

  return AbTestType.DRAFT;
};

/**
 * Format date to display format: "30 Jan 2025"
 */
export const formatDateForDisplay = (date: Date | null): string => {
  if (!date) {
    return '';
  }
  const dateObj = new Date(date);
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  const month = dateObj.toLocaleString('en-US', { month: 'short' });
  return `${day} ${month} ${year}`;
};

/**
 * Format date range for concluded tests: "30 Jan 2025 - 8 Feb 2025"
 */
export const formatAbTestDateRange = (startedAt: Date | null, endedAt: Date | null): string => {
  if (!startedAt || !endedAt) {
    return '';
  }
  const startDate = formatDateForDisplay(startedAt);
  const endDate = formatDateForDisplay(endedAt);
  return `${startDate} - ${endDate}`;
};

export const PREDICTED_SEGMENTS_MAPPING = {
  all_checkouts: 'All Checkouts',
  all_after_shipping_selection: 'All Users',
  US_lower_after_shipping_selection: 'US Lower States',
  returning_users_after_shipping_selection: 'Returning Users',
  new_users_after_shipping_selection: 'New Users',
  with_coupon_after_shipping_selection: 'With Coupon Code',
  without_coupon_after_shipping_selection: 'Without Coupon Code',
  below_fst_after_shipping_selection: 'Below Free Shipping Threshold',
  above_fst_after_shipping_selection: 'Above Free Shipping Threshold',
  paid_traffic_after_shipping_selection: 'Paid Traffic',
  email_sms_traffic_after_shipping_selection: 'Email/SMS Traffic',
  meta_traffic_after_shipping_selection: 'Meta Traffic',
  East_Coast_after_shipping_selection: 'East Coast',
  West_Coast_after_shipping_selection: 'West Coast',
  Midwest_after_shipping_selection: 'Midwest',
  Northeast_after_shipping_selection: 'Northeast',
  Start_of_the_Week_after_shipping_selection: 'Start of the Week',
  Before_the_Weekend_after_shipping_selection: 'Before the Weekend',
  Weekend_after_shipping_selection: 'Weekend',
  With_subscription_after_shipping_selection: 'With Subscription',
  Without_subscription_after_shipping_selection: 'Without Subscription',
  Male_after_shipping_selection: 'Male Users',
  Female_after_shipping_selection: 'Female Users',
  Returning_With_Coupon_after_shipping_selection: 'Returning Users with Coupon',
  New_With_Coupon_after_shipping_selection: 'New Users with Coupon',
  Returning_Without_Coupon_after_shipping_selection: 'Returning Users without Coupon',
  New_Without_Coupon_after_shipping_selection: 'New Users without Coupon',
  Big_Spender_after_shipping_selection: 'Big Spenders',
  Morning: 'Morning',
  Noon: 'Noon',
  Evening: 'Evening',
  Night: 'Night',
};

// Snowflake views lowercase segment keys while the map keys are mixed case —
// the lookup must not care
const PREDICTED_SEGMENTS_MAPPING_BY_LOWERCASE_KEY: Record<string, string> = Object.fromEntries(
  Object.entries(PREDICTED_SEGMENTS_MAPPING).map(([key, label]) => [key.toLowerCase(), label]),
);

export const mapSegmentName = (name: string) => {
  return PREDICTED_SEGMENTS_MAPPING_BY_LOWERCASE_KEY[name?.toLowerCase()] || '-';
};

export const CROSS_SITE_SEGMENTS_MAPPING = {
  all_users: 'All Users',
  homepage_traffic: 'Homepage Traffic',
  english_lang_browser_traffic: 'English Browser Traffic',
  desktop: 'Desktop Users',
  start_of_the_week: 'Start of the Week',
  Morning: 'Morning',
  Noon: 'Noon',
  Evening: 'Evening',
  Night: 'Night',
};

export const mapCrossSiteSegmentName = (name: string) => {
  return CROSS_SITE_SEGMENTS_MAPPING[name] || name;
};

export const SKIP_AB_TEST_PREDICTED_SEGMENTS = [
  'all_checkouts',
  'all_after_shipping_selection',
  'US_lower_after_shipping_selection',
  'paid_traffic_after_shipping_selection',
  'email_sms_traffic_after_shipping_selection',
  'meta_traffic_after_shipping_selection',
];
export const mapAbTestSegmentNameToRules = (
  segment: SegmentMetrics,
  freeShippingThreshold?: number | null,
  bigSpenderThreshold?: number | null,
) => {
  const segmentMapping = {
    returning_users_after_shipping_selection: [
      {
        property: Property.customerOrderCount,
        operator: Operator.is_more_than,
        value: '0',
      },
    ],
    new_users_after_shipping_selection: [
      {
        property: Property.customerOrderCount,
        operator: Operator.equals,
        value: '0',
      },
    ],
    with_coupon_after_shipping_selection: [
      {
        property: Property.hasACoupon,
        operator: Operator.is,
        value: 'true',
      },
    ],
    without_coupon_after_shipping_selection: [
      {
        property: Property.hasACoupon,
        operator: Operator.is,
        value: 'false',
      },
    ],
    below_fst_after_shipping_selection: [
      {
        property: Property.cartValue,
        operator: Operator.is_less_than,
        value: freeShippingThreshold?.toString() || '100',
      },
    ],
    above_fst_after_shipping_selection: [
      {
        property: Property.cartValue,
        operator: Operator.is_more_than,
        value: freeShippingThreshold?.toString() || '100',
      },
    ],
    East_Coast_after_shipping_selection: [
      {
        property: Property.country,
        operator: Operator.is,
        value: JSON.stringify({
          US: [
            'AL',
            'AR',
            'CT',
            'DE',
            'FL',
            'GA',
            'IL',
            'IN',
            'KY',
            'LA',
            'VT',
            'VA',
            'WV',
            'WI',
            'DC',
            'TN',
            'SC',
            'RI',
            'PA',
            'OH',
            'NC',
            'NY',
            'NJ',
            'NH',
            'MS',
            'MI',
            'MA',
            'MD',
            'ME',
          ],
        }),
      },
    ],
    West_Coast_after_shipping_selection: [
      {
        property: Property.country,
        operator: Operator.is,
        value: JSON.stringify({
          US: [
            'AK',
            'AZ',
            'CA',
            'CO',
            'HI',
            'ID',
            'IA',
            'KS',
            'WA',
            'WY',
            'UT',
            'TX',
            'SD',
            'OR',
            'OK',
            'ND',
            'NM',
            'NV',
            'NE',
            'MT',
            'MO',
            'MN',
          ],
        }),
      },
    ],
    Midwest_after_shipping_selection: [
      {
        property: Property.country,
        operator: Operator.is,
        value: JSON.stringify({ US: ['IL', 'IN', 'IA', 'KS', 'MI', 'MN', 'MO', 'NE', 'ND', 'OH', 'SD', 'WI'] }),
      },
    ],
    Northeast_after_shipping_selection: [
      {
        property: Property.country,
        operator: Operator.is,
        value: JSON.stringify({ US: ['CT', 'ME', 'MA', 'NH', 'NJ', 'NY', 'PA', 'RI', 'VT'] }),
      },
    ],
    Start_of_the_Week_after_shipping_selection: [
      {
        property: Property.weekday,
        operator: Operator.is,
        value: JSON.stringify(['monday', 'tuesday']),
      },
    ],
    Before_the_Weekend_after_shipping_selection: [
      {
        property: Property.weekday,
        operator: Operator.is,
        value: JSON.stringify(['wednesday', 'thursday', 'friday']),
      },
    ],
    Weekend_after_shipping_selection: [
      {
        property: Property.weekday,
        operator: Operator.is,
        value: JSON.stringify(['saturday', 'sunday']),
      },
    ],
    With_subscription_after_shipping_selection: [
      {
        property: Property.subscription,
        operator: Operator.is,
        value: 'in_the_cart',
      },
    ],
    Without_subscription_after_shipping_selection: [
      {
        property: Property.subscription,
        operator: Operator.is_not,
        value: 'in_the_cart',
      },
    ],
    Male_after_shipping_selection: [
      {
        property: Property.gender,
        operator: Operator.is,
        value: 'male',
      },
    ],
    Female_after_shipping_selection: [
      {
        property: Property.gender,
        operator: Operator.is,
        value: 'female',
      },
    ],
    Returning_With_Coupon_after_shipping_selection: [
      {
        property: Property.customerOrderCount,
        operator: Operator.is_more_than,
        value: '0',
      },
      {
        property: Property.hasACoupon,
        operator: Operator.is,
        value: 'true',
      },
    ],
    New_With_Coupon_after_shipping_selection: [
      {
        property: Property.customerOrderCount,
        operator: Operator.equals,
        value: '0',
      },
      {
        property: Property.hasACoupon,
        operator: Operator.is,
        value: 'false',
      },
    ],
    Returning_Without_Coupon_after_shipping_selection: [
      {
        property: Property.customerOrderCount,
        operator: Operator.is_more_than,
        value: '0',
      },
      {
        property: Property.hasACoupon,
        operator: Operator.is,
        value: 'false',
      },
    ],
    New_Without_Coupon_after_shipping_selection: [
      {
        property: Property.customerOrderCount,
        operator: Operator.equals,
        value: '0',
      },
      {
        property: Property.hasACoupon,
        operator: Operator.is,
        value: 'false',
      },
    ],
    Big_Spender_after_shipping_selection: [
      {
        property: Property.customerSpend,
        operator: Operator.is_more_than,
        value: bigSpenderThreshold?.toString() || '100',
      },
    ],
    Morning: [
      {
        property: Property.timeOfDay,
        operator: Operator.is,
        value: 'morning',
      },
    ],
    Noon: [
      {
        property: Property.timeOfDay,
        operator: Operator.is,
        value: 'noon',
      },
    ],
    Evening: [
      {
        property: Property.timeOfDay,
        operator: Operator.is,
        value: 'evening',
      },
    ],
    Night: [
      {
        property: Property.timeOfDay,
        operator: Operator.is,
        value: 'night',
      },
    ],
  };

  const rules = segmentMapping[segment.segmentId];
  if (!rules || rules === '-') {
    return '-';
  }

  // The segmentMapping already uses the threshold values with fallback to '100'
  // No need to override again since the values are already set in the mapping above
  return rules;
};

// AB Test Winner DTO Mapping Utilities

/**
 * Convert RuleEngineState to API DTO format for a single segment
 */
export const mapRuleEngineStateToAbTestWinnerDto = (
  ruleEngineState: RuleEngineState,
): AbTestWinnerPredictedSegmentGroupsDto => {
  return {
    matchAllRuleGroups: ruleEngineState.matchAllRuleGroups,
    ruleGroups: ruleEngineState.ruleGroups.map((ruleGroup) => ({
      matchAllRules: ruleGroup.matchAllRules,
      rules: ruleGroup.rules?.map((rule) => {
        const propertyType = getKeyByStringValue(Property, rule.property);
        // Convert gender values from "male"/"female" to "M"/"F"
        let value = rule.value;
        if (propertyType === 'gender' && typeof value === 'string') {
          if (value.toLowerCase() === 'male') {
            value = 'M';
          } else if (value.toLowerCase() === 'female') {
            value = 'F';
          }
        }

        return {
          type: propertyType,
          operator: getKeyByStringValue(Operator, rule.operator),
          value: value,
          measuringUnit: rule.measuringUnit,
          optionalValue: rule.optionalValue,
        };
      }),
      addedFromPredictedSegment: ruleGroup.addedFromPredictedSegment,
    })),
  };
};

/**
 * Create segment pairs from segments (constructs pairs on-demand to ensure they stay together)
 */
export const createAbTestSegmentPairs = (
  segments: SegmentMetrics[],
  freeShippingThreshold?: number | null,
  bigSpenderThreshold?: number | null,
): SegmentWithState[] => {
  return segments.map((segment) => ({
    segment,
    ruleEngineState: {
      matchAllRuleGroups: true,
      ruleGroups: [
        {
          internalId: 0,
          apiId: null,
          matchAllRules: true,
          addedFromPredictedSegment: true,
          rules: mapAbTestSegmentNameToRules(segment, freeShippingThreshold, bigSpenderThreshold),
        },
      ],
    },
  }));
};

/**
 * Create segment groups array from paired data (ensures segment and state are always matched)
 */
export const createAbTestSegmentGroupsArray = (
  abTestName: string,
  segmentPairs: SegmentWithState[],
): AbTestWinnerPredictedSegmentRequestDto[] => {
  return segmentPairs.map(({ segment, ruleEngineState }) => ({
    name: `${abTestName} - ${segment.segmentName}`,
    segmentRuleGroups: [mapRuleEngineStateToAbTestWinnerDto(ruleEngineState)],
  }));
};

/**
 * Create winner variant DTO for API
 */
export const createAbTestWinnerVariantDto = (
  abTestGroupId: number,
  abTestName: string,
  winnerVariantId: number | null,
  selectedActionOption: string | null,
  comments: string,
  selectedSegments: SegmentMetrics[] | null,
  freeShippingThreshold?: number | null,
  bigSpenderThreshold?: number | null,
  outlierFilter?: ABTestOutlierFilterOption,
  originalPredictedSegments?: SegmentMetrics[] | null,
  totalAnnualizedLift?: Record<string, any>[] | null,
  revenueUnlock?: Record<string, any>[] | null,
): AbTestWinnerRequestDto => {
  // Create pairs on-demand from source segments, then create segment groups array
  const segmentPairs =
    selectedSegments && selectedSegments.length > 0
      ? createAbTestSegmentPairs(selectedSegments, freeShippingThreshold, bigSpenderThreshold)
      : [];
  const segmentGroupsArray = segmentPairs.length > 0 ? createAbTestSegmentGroupsArray(abTestName, segmentPairs) : [];

  return {
    winnerVariantId,
    implementABTestWinnerOption: selectedActionOption,
    predictedSegmentGroups: segmentGroupsArray.length > 0 ? segmentGroupsArray : null,
    disableLiveCheckoutGroup: false,
    abTestGroupId,
    abTestComments: comments,
    outlierFilter,
    originalPredictedSegments:
      originalPredictedSegments && originalPredictedSegments.length > 0 ? originalPredictedSegments : null,
    selectedPredictedSegments: selectedSegments && selectedSegments.length > 0 ? selectedSegments : null,
    totalAnnualizedLift,
    revenueUnlock,
  };
};

export const noneExtension: TrafficAllocationVariant = {
  name: 'NONE',
  widgetExternalId: null,
  widgetApiId: null,
  apiId: null,
  percentageTraffic: 0,
  isSelected: false,
  description: 'none',
};
